import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges, TemplateRef,
  ViewChild
} from '@angular/core';
import { UserInfo } from '../shared/user-info.model';
import { DisplayMode } from '../../shared/display-mode';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { ModalService } from '../../shared/services/modal.service';
import { Location } from '@angular/common';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { debounceTime, finalize } from 'rxjs/operators';
import { Region } from '../shared/region.model';
import { LocalLocationContainerLight } from '../../local-loads/shared/local-location-container-light.model';
import {TranslateService} from '@ngx-translate/core';
import { BluechatService } from '../../bluechat/bluechat.service';
import { UserInfoWithNames } from '../user-table/user-table.component';
import { FEATURES, FeatureService } from '../../shared/services/feature.service';
import { EmailValidatorService } from '../../shared/services/email-validator.service';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() user: UserInfo;
  @Input() mode: DisplayMode = DisplayMode.Creation;
  @Input() userTypeLabelKey: string;
  @Input() create: (u: UserInfo) => Observable<UserInfo>;
  @Input() update: (u: UserInfo) => Observable<UserInfo>;
  @Input() block: (u: UserInfo) => Observable<boolean>;
  @Input() unblockIp: (u: UserInfo) => Observable<boolean>;
  @Input() remove: (u: UserInfo) => Observable<boolean>;
  @Input() showRegions = false;
  @Input() showPlants = false;
  @Input() regions: Region[] = [];
  @Input() plants: LocalLocationContainerLight[] = [];
  @Input() canActivateBluechat: boolean;
  @Output() enableBluechat = new EventEmitter<UserInfoWithNames>();
  @Output() afterCreate = new EventEmitter<UserInfo>();
  @Output() afterDelete = new EventEmitter();
  @Output() changePassword = new EventEmitter<string>();
  @ViewChild('userForm') form: NgForm;
  @ViewChild('changePasswordForm') changePasswordForm: NgForm;
  @ViewChild('bluechatActivationModal') bluechatActivationModal: TemplateRef<any>;

  passwordConfirmation: string;
  modalPassword: string;
  modalPasswordConfirmation: string;
  modalFormValid = false;

  formProcessingBlocked = false;
  DisplayMode           = DisplayMode;

  emailSubject = new Subject<void>();
  emailSubscription: Subscription;

  passwordSubject = new Subject<void>();
  passwordSubscription: Subscription;

  changePasswordSubject = new Subject<void>();
  changePasswordSubscription: Subscription;

  bluechatActivationModalFirstname = null;
  bluechatActivationModalLastname = null;

  isActionDropdownOpen = false;

  bluechatEnabled = false;

  userTypeLabel: string;

  constructor(
    public location: Location,
    private authService: AuthenticationService,
    private loader: LoaderService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private bluechatService: BluechatService,
    private featureService: FeatureService,
    private emailValidatorService: EmailValidatorService,
  ) {
    this.emailSubscription = this.emailSubject.pipe(debounceTime(1000))
      .subscribe(() => {
        this.emailValidation(this.form, 'user-email');
      });

    this.passwordSubscription = this.passwordSubject.pipe(debounceTime(1000))
      .subscribe(() => {
        this.passwordValidation(this.form, 'password', 'password-confirm');
      });

    this.changePasswordSubscription = this.changePasswordSubject.pipe(debounceTime(1000))
      .subscribe(() => {
        this.modalPasswordValidation(this.changePasswordForm, 'modal-password', 'modal-password-confirm');
      });

    this.featureService.getFeatureStatusObservable(FEATURES.BLUECHAT).subscribe(bluechatStatus => this.bluechatEnabled = bluechatStatus);
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.user) {
      if (this.user.blocked || this.user.blockDetails.length !== 0) {
        this.blockForm();
      }
    }
    this.userTypeLabel = this.translateService.instant(this.userTypeLabelKey);
  }

  processForm() {
    if (this.mode === DisplayMode.Creation) {
      this.createUser();
    } else {
      this.updateUser();
    }
  }

  blockForm() {
    this.formProcessingBlocked = true;
    this.cdr.detectChanges();
  }

  unblockForm() {
    this.formProcessingBlocked = false;
  }

  emailChanged() {
    this.emailSubject.next();
  }

  passwordChanged() {
    this.passwordSubject.next();
  }

  modalPasswordChanged() {
    this.changePasswordSubject.next();
  }

  emailValidation(form: NgForm, emailFieldName: string) {
    const emailControl = form.form.controls[emailFieldName];

    if (!emailControl) {
      return;
    }

    if (!this.emailValidatorService.isEmailValid(emailControl.value)) {
      emailControl.setErrors({ email: true });
    } else {
      emailControl.updateValueAndValidity();
    }
  }

  passwordValidation(form: NgForm, passwordFieldName: string, passwordConfirmFieldName: string) {
    const passwordControl        = form.form.controls[passwordFieldName];
    const passwordConfirmControl = form.form.controls[passwordConfirmFieldName];

    if (!passwordControl || !passwordConfirmControl) {
      return;
    }

    if (passwordConfirmControl.value !== passwordControl.value) {
      passwordConfirmControl.setErrors({ dismatch: this.translateService.instant('The password does not match') });
    } else {
      passwordConfirmControl.updateValueAndValidity();
    }
  }

  modalPasswordValidation(form: NgForm, passwordFieldName: string, passwordConfirmFieldName: string) {
    this.passwordValidation(form, passwordFieldName, passwordConfirmFieldName);
    this.modalFormValid = form.valid;
  }

  createUser() {
    this.blockForm();
    this.loader.startLoad();

    this.create(this.user)
      .pipe(finalize(() => {
        this.unblockForm();
        this.loader.stopLoad();
      }))
      .subscribe((result: UserInfo) => {
        this.form.form.markAsPristine();
        this.user = result;
        this.afterCreate.emit(result);
        this.toastr.success(this.translateService.instant('user-dashboard.users.form.@create-success', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.success'));
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error?.message || this.translateService.instant('user-dashboard.users.form.error'), this.translateService.instant('user-dashboard.users.form.@create-error', {user: this.userTypeLabel}));
      });
  }

  updateUser() {
    this.blockForm();
    this.loader.startLoad();

    this.update(this.user)
      .pipe(finalize(() => {
        this.unblockForm();
        this.loader.stopLoad();
      }))
      .subscribe((result: UserInfo) => {
        this.form.form.markAsPristine();
        this.user = result;
        this.toastr.success(this.translateService.instant('user-dashboard.users.form.@update-success', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.success'));
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error?.message || this.translateService.instant('user-dashboard.users.form.error'), this.translateService.instant('user-dashboard.users.form.@update-error', {user: this.userTypeLabel}));
      });
  }

  openDeleteConfirm(content) {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;

    this.modalService.open({
      title: 'Delete User',
      content,
      closeButton: {
        text: 'No, Keep it'
      },
      validateButton: {
        type: 'danger',
        text: 'Yes, Delete it',
        icon: 'fa fa-trash'
      }
    }).subscribe(() => {
      this.form.form.markAsPristine();
      this.deleteUser();
    });
  }

  deleteUser() {
    this.blockForm();
    this.loader.startLoad();

    this.remove(this.user)
      .pipe(finalize(() => {
        this.unblockForm();
        this.loader.stopLoad();
      }))
      .subscribe((res: boolean) => {
        if (res) {
          this.toastr.success(this.translateService.instant('user-dashboard.users.form.@delete-success', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.success'));
          this.afterDelete.emit();
        } else {
          this.toastr.error(this.translateService.instant('user-dashboard.users.form.@delete-forbidden', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));
        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(this.translateService.instant('user-dashboard.users.form.@delete-error', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));
      });
  }

  toggleBlockingUser(): void {
    if (this.user.id === this.authService.getUserProfile().id) {
      this.toastr.warning(this.translateService.instant('user-dashboard.users.form.own-account-forbidden'));
      return;
    }

    this.isActionDropdownOpen = !this.isActionDropdownOpen;
    this.blockForm();
    this.loader.startLoad();

    this.block(this.user)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(res => {
        if (res) {
          this.user.blocked = !this.user.blocked;

          if (!this.user.blocked) {
            this.unblockForm();
          }

          this.toastr.success(this.translateService.instant(this.user.blocked?'user-dashboard.users.form.@unblock-success':'user-dashboard.users.form.@block-success', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.success'));
        } else {
          this.toastr.error(this.translateService.instant(this.user.blocked?'user-dashboard.users.form.@unblock-forbidden':'user-dashboard.users.form.@block-forbidden', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));

        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(this.translateService.instant(this.user.blocked?'user-dashboard.users.form.@unblock-error':'user-dashboard.users.form.@block-error', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));
      });
  }

  unblockUserIp(): void {
    if (this.user.id === this.authService.getUserProfile().id) {
      this.toastr.warning(this.translateService.instant('user-dashboard.users.form.own-account-forbidden'));
      return;
    }
    this.isActionDropdownOpen = !this.isActionDropdownOpen;
    this.blockForm();
    this.loader.startLoad();

    this.unblockIp(this.user)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(res => {
        if (res) {
          this.user.blockDetails = [];
          if (this.user.blockDetails.length === 0) {
            this.unblockForm();
          }

          this.toastr.success(this.translateService.instant('user-dashboard.users.form.@unblock-success', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.success'));
        } else {
          this.toastr.error(this.translateService.instant('user-dashboard.users.form.@unblock-forbidden', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));

        }
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error.message || this.translateService.instant('user-dashboard.users.form.@unblock-error', {user: this.userTypeLabel}), this.translateService.instant('user-dashboard.users.form.error'));
      });
  }

  openChangePasswordConfirm(content) {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;

    this.modalPassword             = null;
    this.modalPasswordConfirmation = null;

    this.modalService.open({
      title: this.translateService.instant('user-dashboard.users.form.password.change'),
      content,
      validateButton: {
        text: this.translateService.instant('user-dashboard.users.form.password.update'),
        icon: 'fa fa-save',
        disabled: () => !this.modalFormValid
      }
    }).subscribe(() => this.changePassword.emit(this.modalPassword));
  }

  changeRegions() {
  }

  ngOnDestroy() {
    this.emailSubscription.unsubscribe();
    this.passwordSubscription.unsubscribe();
    this.changePasswordSubscription.unsubscribe();
  }

  customPlantSearch(searchTerm: string, item: LocalLocationContainerLight) {
    searchTerm = searchTerm.toLocaleLowerCase();
    let glidIndex = item.glid ? item.glid.toLocaleLowerCase().indexOf(searchTerm) : -1;
    let nameIndex = item.name ? item.name.toLocaleLowerCase().indexOf(searchTerm) : -1;

    return glidIndex > -1 || nameIndex > -1;
  }

  openBluechatActivationModal(user: UserInfo) {
    const firstnameAndLastname = this.bluechatService.deduceFirstnameAndLastNameFromEmail(user);
    this.bluechatActivationModalFirstname = firstnameAndLastname.firstname;
    this.bluechatActivationModalLastname = firstnameAndLastname.lastname;
    this.modalService.open({
      title: this.translateService.instant('bluechat.modal.title'),
      content: this.bluechatActivationModal,
      data: {
        user,
      },
    }).subscribe( () => {
      this.enableBluechat.emit({
        user,
        firstname: this.bluechatActivationModalFirstname,
        lastname: this.bluechatActivationModalLastname,
      });
    });
  }

  atLeastOnePlantHasCoordinatesNotValidated() {
    return this.user.allowedPlants.some(plantId => {
      let plant = this.plants.find(p => p.id === plantId);
      return this.hasCoordinatesNotValidated(plant);
    });
  }

  hasCoordinatesNotValidated(item: LocalLocationContainerLight) {
    return item ? !item.coordinatesValidated : false;
  }
}
